import { KindeProvider, useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { config } from "@noted/configuration";
import { captureException, captureMessage } from "@sentry/react";
import { ComponentProps, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { restPoster } from "~/api/fetchers";
import AuthProvider, { UseAuthType } from "~/components/auth-provider/index";
import { clearContextSession, getContextSession } from "~/components/auth-provider/shared";
import { isDevelopment } from "~/utilities/is-development";

export function KindeAuthProvider({ children }: ComponentProps<typeof AuthProvider>) {
  const navigate = useNavigate();
  return (
    <KindeProvider
      audience={config.auth.audience}
      clientId={config.auth.clientId}
      domain={config.auth.domain}
      redirectUri={config.appUrl}
      logoutUri={config.landingUrl}
      useInsecureForRefreshToken={isDevelopment()}
      callbacks={{
        onSuccess: (_user, state) => {
          if (state?.redirectTo && typeof state.redirectTo === "string") {
            navigate(decodeURIComponent(state.redirectTo));
          }
        },
        onError: (error, _state, context) => {
          captureException(error, { tags: { context: "kinde_auth_error" }, extra: { context } });
        },
      }}
    >
      {children}
    </KindeProvider>
  );
}

export function useKindeAuthHook(): Omit<UseAuthType, "session"> {
  const { isAuthenticated, isLoading, getAccessToken, login, logout: kindeLogout } = useKindeAuth();

  const getToken = useCallback(async () => {
    try {
      const token = await getAccessToken();
      if (!token) {
        captureMessage("Kinde getAccessToken returned null/undefined", { level: "warning" });
      }
      return token ?? "";
    } catch (err) {
      captureException(err, { tags: { context: "kinde_get_token" } });
      return "";
    }
  }, [getAccessToken]);

  const logout = useCallback(async () => {
    try {
      if (getContextSession()) {
        await restPoster("/v1/auth/logout");
      }
    } finally {
      clearContextSession();
      await kindeLogout({ allSessions: true });
    }
  }, [restPoster, clearContextSession, kindeLogout, config.landingUrl]);

  return {
    isAuthenticated,
    isLoading,
    getToken,
    login,
    logout,
  };
}
